import { useState, useEffect } from "react";
import toast from "react-hot-toast";
import * as services from "../services/services";
import { useAuthStore } from "@/store/authStore";
import { TABS, INITIAL_FORM_STATE } from "../utils/constants";
import { validateForm } from "../utils/validation";
import { getFilteredData as filterData } from "../utils/filters";

export const useInventorySettings = () => {
  const [activeTab, setActiveTab] = useState("category"); // values: category, unit, supplier, brand
  const [search, setSearch] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [viewingItem, setViewingItem] = useState(null);
  const [loading, setLoading] = useState(false);

  // Data from Backend
  const [data, setData] = useState([]);
  const [gstRegisteredTypes, setGstRegisteredTypes] = useState([]);
  const [buyers, setBuyers] = useState([]);
  const [stores, setStores] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [units, setUnits] = useState([]);
  const [errors, setErrors] = useState({});
  const [isPurchaseFormOpen, setIsPurchaseFormOpen] = useState(false);
  const { user } = useAuthStore();
  
  const [form, setForm] = useState(INITIAL_FORM_STATE);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      let res;
      if (activeTab === "purchase") {
        res = await services.getPurchases();
      } else {
        const mappedTab = activeTab === "supplier" ? "vendor" : activeTab;
        res = await services.getInventorySettings({ settingType: mappedTab, limit: 0 });
      }

      if (res.status === "success") {
        setData(res.data);
      }
    } catch (err) {
      toast.error("Failed to fetch settings");
    } finally {
      setLoading(false);
    }
  };

  const fetchDropdownData = async () => {
    try {
      // Fetch GST registered types
      const gstRes = await services.getInventorySettings({
        settingType: "registeredType", limit: 0
      });
      if (gstRes.status === "success") {
        const gstOptions = gstRes.data.map((g) => {
          const gTitle = g.registeredTypeName || g.title || "";
          const gCode = g.registeredTypeCode || g.code || "";
          return {
            ...g,
            label: gCode ? `${gTitle} (${gCode})` : gTitle || "—",
          };
        });
        setGstRegisteredTypes(gstOptions);
      }

      // Fetch vendors for dropdown
      const vendorRes = await services.getInventorySettings({
        settingType: "vendor", limit: 0
      });
      if (vendorRes.status === "success") {
        const vendorOptions = vendorRes.data.map((v) => {
          const vTitle = v.vendorName || v.name || v.title || "";
          const vCode = v.vendorCode || v.code || "";
          return {
            ...v,
            label: vCode ? `${vTitle} (${vCode})` : vTitle || "—",
          };
        });
        setVendors(vendorOptions);
      }

      // Fetch categories for item form
      const categoryRes = await services.getInventorySettings({
        settingType: "category", limit: 0
      });
      if (categoryRes.status === "success") {
        const categoryOptions = categoryRes.data.map((c) => {
          const cTitle = c.categoryName || c.title || "";
          const cCode = c.categoryCode || c.code || "";
          return {
            ...c,
            label: cCode ? `${cTitle} (${cCode})` : cTitle || "—",
          };
        });
        setCategories(categoryOptions);
      }

      // Fetch brands for item form
      const brandRes = await services.getInventorySettings({
        settingType: "brand", limit: 0
      });
      if (brandRes.status === "success") {
        const brandOptions = brandRes.data.map((b) => {
          const bTitle = b.brandName || b.title || "";
          const bCode = b.brandCode || b.code || "";
          return {
            ...b,
            label: bCode ? `${bTitle} (${bCode})` : bTitle || "—",
          };
        });
        setBrands(brandOptions);
      }

      // Fetch units for item form
      const unitRes = await services.getInventorySettings({
        settingType: "unit", limit: 0
      });
      if (unitRes.status === "success") {
        const unitOptions = unitRes.data.map((u) => {
          const uTitle = u.unitName || u.title || "";
          const uCode = u.unitCode || u.code || "";
          return {
            ...u,
            label: uCode ? `${uTitle} (${uCode})` : uTitle || "—",
          };
        });
        setUnits(unitOptions);
      }

      // Fetch stores for purchase form
      // const storeRes = await services.getInventorySettings({
      //   settingType: "stores", limit: 0
      // });
      // if (storeRes.status === "success") {
      //   setStores(storeRes.data);
      // }
    } catch (err) {
      console.error("Failed to fetch dropdown data:", err);
      toast.error("Failed to load dropdown lists (Categories, Brands, etc.)");
    }
  };

  useEffect(() => {
    fetchSettings();
    fetchDropdownData();
  }, []);

  useEffect(() => {
    if (!isModalOpen) {
      fetchSettings();
    } else {
      fetchDropdownData();
    }
  }, [activeTab, isModalOpen]);

  const handleAdd = () => {
    setEditingItem(null);
    setErrors({});
    setForm({ ...INITIAL_FORM_STATE });

    if (activeTab === "purchase") {
      setIsPurchaseFormOpen(true);
    } else {
      setIsModalOpen(true);
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setErrors({});

    const flatten = (val) =>
      typeof val === "object" && val !== null ? val._id : val || "";

    setForm({
      ...item,
      title: item.title || item.registeredTypeName || item.unitName || item.brandName || item.categoryName || item.storeName || item.name || item.vendorName || "",
      code: item.code || item.registeredTypeCode || item.unitCode || item.brandCode || item.categoryCode || item.storeCode || item.vendorCode || item.itemCode || "",
      name: item.name || item.vendorName || "",
      address: item.address || item.addressLine1 || "",
      mobile: item.mobile || "",
      email: item.email || "",
      state: item.state || "",
      openingDate: item.openingDate || (item.createdDate ? new Date(item.createdDate).toISOString().split('T')[0] : (item.createdAt ? new Date(item.createdAt).toISOString().split('T')[0] : "")),
      district: item.district || item.city || "",
      gstRegisteredType: item.gstRegisteredType || item.gstType || "",
      gstNo: item.gstNo || "",
      bankIfsc: item.bankIfsc || item.ifscCode || "",
      bankName: item.bankName || "",
      bankBranch: item.bankBranch || item.branchName || "",
      bankAccountHolder: item.bankAccountHolder || item.accountHolderName || "",
      bankAccountNo: item.bankAccountNo || item.accountNumber || "",
      category: flatten(item.category),
      brand: flatten(item.brand),
      units: flatten(item.units),
      vendor: flatten(item.vendor),
      active: item.status ? item.status === 'ACTIVE' : (item.active !== undefined ? item.active : true),
      fixingType: item.fixingType || "",
      moq: item.moq || "",
      colour: item.colour || "",
      profile: item.profile || "",
      width: item.width || "",
      height: item.height || "",
      thickness: item.thickness || "",
      quality: item.quality || "",
    });

    if (activeTab === "purchase") {
      setIsPurchaseFormOpen(true);
    } else {
      setIsModalOpen(true);
    }
  };

  const handleSave = async () => {
    const validationErrors = validateForm(form, activeTab);
    const errorFields = Object.keys(validationErrors);

    if (errorFields.length > 0) {
      setErrors(validationErrors);
      errorFields
        .slice(0, 3)
        .forEach((field) => toast.error(validationErrors[field]));
      return;
    }

    try {
      const payload = { ...form, settingType: activeTab === 'supplier' ? 'vendor' : activeTab };

      if (activeTab === "item") {
        if (payload.image && payload.image.startsWith('data:image/')) {
          const res = await fetch(payload.image);
          const blob = await res.blob();
          const formData = new FormData();
          formData.append('image', blob, 'item_image.png');
          
          try {
            const uploadRes = await services.uploadItemImage(formData);
            payload.image = uploadRes.fileId;
          } catch (uploadError) {
            console.error("Failed to upload image", uploadError);
            toast.error("Failed to upload image");
            return;
          }
        }

        payload.code = form.itemCode;
      } else if (activeTab === "registeredType") {
        payload.registeredTypeName = form.title;
        payload.registeredTypeCode = form.code;
        payload.status = form.active ? 'ACTIVE' : 'INACTIVE';
      } else if (activeTab === "unit") {
        payload.unitName = form.title;
        payload.unitCode = form.code;
        payload.status = form.active ? 'ACTIVE' : 'INACTIVE';
      } else if (activeTab === "brand") {
        payload.brandName = form.title;
        payload.brandCode = form.code;
        payload.status = form.active ? 'ACTIVE' : 'INACTIVE';
      } else if (activeTab === "category") {
        payload.categoryName = form.title;
        payload.categoryCode = form.code;
        payload.status = form.active ? 'ACTIVE' : 'INACTIVE';
      } else if (activeTab === "stores") {
        payload.storeName = form.title;
        payload.storeCode = form.code;
        payload.addressLine1 = form.address;
        payload.status = form.active ? 'ACTIVE' : 'INACTIVE';
      } else if (activeTab === "supplier") {
        payload.vendorName = form.name;
        payload.vendorCode = form.code;
        payload.addressLine1 = form.address;
        payload.city = form.district;
        payload.gstType = form.gstRegisteredType;
        payload.ifscCode = form.bankIfsc;
        payload.branchName = form.bankBranch;
        payload.accountHolderName = form.bankAccountHolder;
        payload.accountNumber = form.bankAccountNo;
        payload.status = form.active ? 'ACTIVE' : 'INACTIVE';
      }

      if (payload.vendor === "") {
        delete payload.vendor;
      }
      if (editingItem) {
        await services.updateInventorySetting(editingItem._id, payload);
        toast.success("Updated successfully");
      } else {
        await services.createInventorySetting(payload);
        toast.success("Added successfully");
      }
      setIsModalOpen(false);
      setErrors({});
      fetchSettings();
      fetchDropdownData();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save");
    }
  };

  const handleInputChange = (e) => {
    const field = e.target.name || e.target.id;
    let value =
      e.target.type === "checkbox" ? e.target.checked : e.target.value;

    if (field === "mobile") {
      value = value.replace(/\D/g, "").slice(0, 10);
    }
    if (field === "bankAccountNo") {
      value = value.replace(/\D/g, "").slice(0, 18);
    }
    if (field === "bankIfsc" || field === "gstNo") {
      value = value.toUpperCase().replace(/\s/g, "");
    }
    const numericFields = [];
    if (numericFields.includes(field)) {
      value = value.replace(/[^0-9.]/g, "");
      const parts = value.split(".");
      if (parts.length > 2) value = parts[0] + "." + parts.slice(1).join("");
    }

    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this?")) {
      try {
        let res;
        if (activeTab === "purchase") {
          res = await services.deletePurchase(id);
        } else {
          const mappedTab = activeTab === "supplier" ? "vendor" : activeTab;
          res = await services.deleteInventorySetting(id, mappedTab);
        }

        if (res.status === "success") {
          toast.success("Deleted successfully");
          fetchSettings();
        }
      } catch (err) {
        toast.error("Failed to delete");
      }
    }
  };

  const getFilteredData = () => {
    return filterData(data, search, activeTab);
  };

  return {
    activeTab, setActiveTab,
    search, setSearch,
    isModalOpen, setIsModalOpen,
    editingItem, setEditingItem,
    viewingItem, setViewingItem,
    loading, setLoading,
    data, setData,
    gstRegisteredTypes, setGstRegisteredTypes,
    buyers, setBuyers,
    stores, setStores,
    vendors, setVendors,
    categories, setCategories,
    brands, setBrands,
    units, setUnits,
    errors, setErrors,
    isPurchaseFormOpen, setIsPurchaseFormOpen,
    user,
    form, setForm,
    TABS,
    fetchSettings,
    fetchDropdownData,
    handleAdd,
    handleEdit,
    handleSave,
    handleInputChange,
    handleDelete,
    getFilteredData
  };
};
